import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { X, Save, Package, User, Calendar, MapPin, Search } from 'lucide-react';

const LoanForm = ({ currentLoan, onSave, onCancel, products }) => {
  const [formData, setFormData] = useState({
    product_id: '',
    quantity: 1,
    borrower_name: '',
    borrower_contact: '',
    barcode: '',
    purpose: '',
    requisition: '',
    expected_return_date: '',
    notes: '',
    workshop: ''
  });

  // Estado para el buscador de productos
  const [productSearch, setProductSearch] = useState('');
  const [showProductList, setShowProductList] = useState(false);
  const [selectedProductName, setSelectedProductName] = useState('');

  useEffect(() => {
    if (currentLoan) {
      setFormData({
        product_id: currentLoan.product_id || '',
        quantity: currentLoan.quantity || 1,
        borrower_name: currentLoan.borrower_name || '',
        borrower_contact: currentLoan.borrower_contact || '',
        barcode: currentLoan.barcode || '',
        purpose: currentLoan.purpose || '',
        requisition: currentLoan.requisition || '',
        expected_return_date: currentLoan.expected_return_date 
          ? new Date(currentLoan.expected_return_date).toISOString().split('T')[0] 
          : '',
        notes: currentLoan.notes || ''
        ,
        workshop: currentLoan.workshop || ''
      });
      
      // Establecer el nombre del producto seleccionado y mostrarlo en el input
      if (currentLoan.product_id) {
        const product = products.find(p => p.id === currentLoan.product_id);
        if (product) {
          setSelectedProductName(product.name);
          setProductSearch(product.name);
        }
      }
    }
  }, [currentLoan, products]);


  const barcodeRef = useRef(null);
  // States to support pistol-scanner global input like other forms
  const [barcodeBuffer, setBarcodeBuffer] = React.useState('');
  const [lastKeypressTime, setLastKeypressTime] = React.useState(0);
  const [productSearchMap, setProductSearchMap] = React.useState({});

  // Cerrar lista de productos al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.product-search-container')) {
        setShowProductList(false);
      }
    };

    if (showProductList) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showProductList]);

  // Focus al abrir el modal para recibir escaneo de pistola (emula teclado)
  useEffect(() => {
    try {
      if (barcodeRef.current) {
        barcodeRef.current.focus();
      }
    } catch (e) {
      // no-op
    }
  }, []);

  // Build simple search map from products for quick lookup
  useEffect(() => {
    const map = {};
    (products || []).forEach(p => {
      // barcode
      const barcodeVal = p.barcode || p.bar_code || p.product_barcode;
      if (barcodeVal) {
        const k = String(barcodeVal).trim().toLowerCase();
        map[k] = p;
        map[k.replace(/'/g, '-')] = p;
        map[k.replace(/,/g, '-')] = p;
        map[k.replace(/[^a-z0-9]/g, '')] = p;
      }

      // sku variants
      const skuVal = p.sku || p.product_sku || p.productSku || p.sku_code;
      if (skuVal) {
        const k = String(skuVal).trim().toLowerCase();
        map[k] = p;
        map[k.replace(/'/g, '-')] = p;
        map[k.replace(/,/g, '-')] = p;
        map[k.replace(/[^a-z0-9]/g, '')] = p;
      }

      // code / internal code
      const codeVal = p.code || p.product_code;
      if (codeVal) {
        const k = String(codeVal).trim().toLowerCase();
        map[k] = p;
        map[k.replace(/'/g, '-')] = p;
        map[k.replace(/,/g, '-')] = p;
        map[k.replace(/[^a-z0-9]/g, '')] = p;
      }
    });
    setProductSearchMap(map);
  }, [products]);

  // Global key listener to capture pistol-scanner input like in other forms
  useEffect(() => {
    const MAX_SCAN_GAP = 50;
    const SCAN_TIMEOUT = 300;
    const MIN_BARCODE_LENGTH = 3;

    const sanitize = (s) => String(s || '').replace(/['"\\]/g, '');

    const normalizeSpecificCodes = (code) => {
      if (!code) return '';
      let normalized = code;
      // replace commas by hyphens, single quotes by hyphens
      normalized = normalized.replace(/,/g, '-').replace(/'/g, '-');
      // collapse multiple hyphens
      normalized = normalized.replace(/-{2,}/g, '-');
      return normalized;
    };

    const processBarcodeInput = () => {
      if (barcodeBuffer.length < MIN_BARCODE_LENGTH) {
        setBarcodeBuffer('');
        return;
      }

      const raw = barcodeBuffer;
      const sanitized = sanitize(raw).trim().toLowerCase();
      const normalized = normalizeSpecificCodes(raw).trim().toLowerCase();

      const keys = [sanitized, normalized, raw.trim().toLowerCase(), raw.replace(/[^a-z0-9]/gi, '').toLowerCase()];

      let found = null;
      // Fast lookup using the prebuilt search map (includes barcode, sku and code variants)
      for (const k of keys) {
        if (productSearchMap[k]) {
          found = productSearchMap[k];
          break;
        }
      }

      if (found) {
        setFormData(prev => ({ ...prev, product_id: String(found.id), barcode: raw }));
        setSelectedProductName(found.name);
        setProductSearch(found.name);
      }

      setBarcodeBuffer('');
    };

    const handleGlobalKeyDown = (event) => {
      const activeElement = document.activeElement;
      const isInputActive = activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA' || activeElement.isContentEditable);

      // Let normal typing work when user is focused on inputs; still capture Enter on inputs
      if (isInputActive && event.key !== 'Enter') return;

      // ignore modifiers
      if (event.ctrlKey || event.altKey || event.metaKey) return;

      const currentTime = Date.now();
      const timeSinceLastKey = currentTime - lastKeypressTime;

      if (timeSinceLastKey > MAX_SCAN_GAP && barcodeBuffer.length > 0 && timeSinceLastKey > SCAN_TIMEOUT) {
        setBarcodeBuffer('');
      }

      if (event.key === 'Enter') {
        event.preventDefault();
        processBarcodeInput();
      } else {
        setBarcodeBuffer(prev => prev + event.key);
      }

      setLastKeypressTime(currentTime);
    };

    document.addEventListener('keydown', handleGlobalKeyDown);
    return () => document.removeEventListener('keydown', handleGlobalKeyDown);
  }, [barcodeBuffer, lastKeypressTime, productSearchMap]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBarcodeChange = (e) => {
    const value = (e.target.value || '').trim();
    setFormData(prev => ({ ...prev, barcode: value }));

    if (!value) return;

    // Intentar buscar producto por barcode o por sku/código
    try {
      // Fast path: try lookup in productSearchMap using several normalized keys
      const mk = (s) => String(s || '').trim().toLowerCase();
      const candidates = [mk(value), mk(value).replace(/'/g, '-'), mk(value).replace(/,/g, '-'), mk(value).replace(/[^a-z0-9]/g, '')];
      for (const key of candidates) {
        if (productSearchMap[key]) {
          const p = productSearchMap[key];
          setFormData(prev => ({ ...prev, product_id: String(p.id) }));
          setSelectedProductName(p.name);
          setProductSearch(p.name);
          return;
        }
      }

      // Normalizar: eliminar separadores y caracteres no alfanuméricos, pasar a mayúsculas
      const normalize = (s) => String(s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
      const normalizePunct = (s) => String(s || '').toUpperCase().replace(/[,'\s]/g, '-'); // map commas and spaces to hyphen
      const needle = normalize(value);
      const needlePunct = normalizePunct(value);

      // Buscar por cada campo por separado para asegurarnos que sku se contempla
      let found = products.find(p => {
        const bar = p.barcode || p.bar_code || p.product_barcode || '';
        const code = p.code || p.product_code || '';
        const sku = p.sku || p.product_sku || p.productSku || '';

        const barN = normalize(bar);
        const codeN = normalize(code);
        const skuN = normalize(sku);

        return (barN === needle) || (codeN === needle) || (skuN === needle) ||
               (String(bar || '').toUpperCase() === needlePunct) ||
               (String(code || '').toUpperCase() === needlePunct) ||
               (String(sku || '').toUpperCase() === needlePunct);
      });

      // Fallback: intentar comparación menos estricta (comparar quitando solo algunos separadores)
      if (!found) {
        const loose = (s) => String(s || '').toUpperCase().replace(/[\s'_,\-.]/g, '');
        const needleLoose = loose(value);
        found = products.find(p => {
          const bar = p.barcode || p.bar_code || p.product_barcode || '';
          const code = p.code || p.product_code || '';
          const sku = p.sku || p.product_sku || p.productSku || '';
          return loose(bar) === needleLoose || loose(code) === needleLoose || loose(sku) === needleLoose ||
                 (String(bar).toUpperCase().replace(/,/g, '-') === needlePunct) ||
                 (String(code).toUpperCase().replace(/,/g, '-') === needlePunct) ||
                 (String(sku).toUpperCase().replace(/,/g, '-') === needlePunct);
        });
      }
      if (found) {
        setFormData(prev => ({ ...prev, product_id: String(found.id) }));
        setSelectedProductName(found.name);
        setProductSearch(found.name);
      }
    } catch (err) {
      console.warn('Error buscando producto por código de barras:', err);
    }
  };

  const clearBarcode = () => {
    setFormData(prev => ({ ...prev, barcode: '' }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validaciones
    if (!formData.product_id) {
      alert('Por favor selecciona un producto');
      return;
    }
    
    if (!formData.borrower_name.trim()) {
      alert('Por favor ingresa el nombre del prestatario');
      return;
    }
    
    if (formData.quantity < 1) {
      alert('La cantidad debe ser mayor a 0');
      return;
    }
    
    if (!formData.expected_return_date) {
      alert('Por favor selecciona una fecha de devolución esperada');
      return;
    }

    // Verificar stock disponible solo para préstamos nuevos
    if (!currentLoan) {
      const selectedProduct = products.find(p => p.id.toString() === formData.product_id);
      if (selectedProduct && selectedProduct.stock < formData.quantity) {
        alert(`Stock insuficiente. Disponible: ${selectedProduct.stock}`);
        return;
      }
    }

    onSave(formData);
  };

  const getSelectedProduct = () => {
    return products.find(p => p.id.toString() === formData.product_id);
  };

  return (
    <motion.div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-3xl p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Package className="w-7 h-7 text-blue-600" />
            {currentLoan ? 'Editar Préstamo' : 'Nuevo Préstamo'}
          </h3>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Código de Barras / Escáner */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Package className="w-4 h-4" />
              Escanear Código / Código de Barras
            </label>
            <div className="flex gap-2">
              <input
                ref={barcodeRef}
                type="text"
                name="barcode"
                value={formData.barcode}
                onChange={handleBarcodeChange}
                placeholder="Escanea con la pistola o escribe el código"
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button type="button" onClick={clearBarcode} className="px-3 py-2 bg-gray-100 rounded-xl">Limpiar</button>
            </div>
            <p className="text-xs text-gray-500">La pistola escanea como teclado: apunta al input y escanea para seleccionar el producto automáticamente.</p>
          </div>
          {/* Selección de Producto con Buscador */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Package className="w-4 h-4" />
              Buscar Producto
            </label>
            <div className="relative product-search-container">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={productSearch}
                  onChange={(e) => {
                    setProductSearch(e.target.value);
                    setShowProductList(true);
                  }}
                  onFocus={() => setShowProductList(true)}
                  placeholder="Buscar por nombre, SKU o código..."
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              {/* Lista de productos filtrados */}
              {showProductList && (
                <div className="absolute z-50 w-full mt-2 bg-white border border-gray-300 rounded-xl shadow-lg max-h-96 overflow-y-auto">
                  {(() => {
                    const search = productSearch.toLowerCase().trim();
                    const filteredProducts = products.filter(p => {
                      // Si no hay búsqueda, mostrar todos
                      if (!search) return true;
                      
                      // Filtrar por búsqueda
                      return (
                        p.name?.toLowerCase().includes(search) ||
                        p.sku?.toLowerCase().includes(search) ||
                        p.code?.toLowerCase().includes(search) ||
                        p.barcode?.toLowerCase().includes(search)
                      );
                    });

                    console.log('🔍 Productos disponibles:', products.length);
                    console.log('🔍 Productos filtrados:', filteredProducts.length);
                    console.log('🔍 Búsqueda:', search);

                    if (filteredProducts.length === 0) {
                      return (
                        <div className="px-4 py-8 text-center text-gray-500">
                          <Package className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                          <p>No se encontraron productos</p>
                          <p className="text-xs mt-2">Total de productos: {products.length}</p>
                        </div>
                      );
                    }

                    return filteredProducts.map(product => (
                      <div
                        key={product.id}
                        onClick={() => {
                          setFormData({ ...formData, product_id: product.id });
                          setSelectedProductName(product.name);
                          setProductSearch(product.name);
                          setShowProductList(false);
                        }}
                        className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      >
                        <div className="flex justify-between items-center">
                          <div className="flex-1">
                            <p className="font-medium text-gray-900">{product.name}</p>
                            <p className="text-sm text-gray-600">
                              SKU: {product.sku || 'N/A'} | Código: {product.code || 'N/A'}
                            </p>
                          </div>
                          <div className="text-right ml-4">
                            <p className={`font-semibold ${
                              (product.current_stock || product.stock || 0) > 0 
                                ? 'text-green-600' 
                                : 'text-red-600'
                            }`}>
                              Stock: {product.current_stock || product.stock || 0}
                            </p>
                          </div>
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              )}
            </div>
            
            {/* Producto seleccionado */}
            {selectedProductName && getSelectedProduct() && (
              <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                <p className="font-medium text-blue-900 mb-2">
                  ✓ Producto seleccionado: {selectedProductName}
                </p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-600">SKU:</span>{' '}
                    <span className="font-medium">{getSelectedProduct().sku || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Stock:</span>{' '}
                    <span className={`font-medium ${
                      (getSelectedProduct().current_stock || getSelectedProduct().stock || 0) > 0
                        ? 'text-green-600'
                        : 'text-red-600'
                    }`}>
                      {getSelectedProduct().current_stock || getSelectedProduct().stock || 0}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Cantidad */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Package className="w-4 h-4" />
              Cantidad
            </label>
            <input
              type="number"
              name="quantity"
              value={formData.quantity}
              onChange={handleChange}
              min="1"
              max={getSelectedProduct() ? getSelectedProduct().stock : undefined}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* Información del Prestatario */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <User className="w-4 h-4" />
                Nombre del Prestatario
              </label>
              <input
                type="text"
                name="borrower_name"
                value={formData.borrower_name}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <User className="w-4 h-4" />
                Contacto
              </label>
              <input
                type="text"
                name="borrower_contact"
                value={formData.borrower_contact}
                onChange={handleChange}
                placeholder="Teléfono, email, etc."
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Taller / Workshop */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <MapPin className="w-4 h-4" />
              Taller / Departamento
            </label>
            {/* Input editable con datalist para permitir escritura libre y sugerencias */}
            <input
              type="text"
              name="workshop"
              value={formData.workshop}
              onChange={handleChange}
              list="workshop-list"
              placeholder="Escribe o selecciona un taller (opcional)"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <datalist id="workshop-list">
              {Array.from(new Set(products.map(p => p.workshop || p.taller).filter(Boolean))).map((w, idx) => (
                <option key={`w-${idx}`} value={w} />
              ))}
            </datalist>
          </div>

          {/* Propósito */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <MapPin className="w-4 h-4" />
              Propósito del Préstamo
            </label>
            <input
              type="text"
              name="purpose"
              value={formData.purpose}
              onChange={handleChange}
              placeholder="Ej: Reparación de unidad 123, Mantenimiento preventivo..."
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Requisición */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <MapPin className="w-4 h-4" />
              Número de Requisición
            </label>
            <input
              type="text"
              name="requisition"
              value={formData.requisition}
              onChange={handleChange}
              placeholder="Número de requisición"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Fecha de Devolución Esperada */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Calendar className="w-4 h-4" />
              Fecha de Devolución Esperada
            </label>
            <input
              type="date"
              name="expected_return_date"
              value={formData.expected_return_date}
              onChange={handleChange}
              min={new Date().toISOString().split('T')[0]}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* Notas */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Notas Adicionales
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="3"
              placeholder="Observaciones, condiciones especiales, etc."
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Botones */}
          <div className="flex gap-4 pt-4">
            <motion.button
              type="submit"
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Save className="w-5 h-5" />
              {currentLoan ? 'Actualizar Préstamo' : 'Crear Préstamo'}
            </motion.button>
            
            <motion.button
              type="button"
              onClick={onCancel}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Cancelar
            </motion.button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

export default LoanForm;